import { useState, useEffect } from 'react';
import { UserRole } from '../../services/authService';
import { ProfileData } from '../../services/profileService';
// Using inline SVG for XMarkIcon to avoid dependency issues
const XMarkIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

interface ProfileEditProps {
  profile: ProfileData;
  role: UserRole;
  onSave: (data: Partial<ProfileData>) => Promise<void>;
  onCancel: () => void;
}

const ProfileEdit = ({ profile, role, onSave, onCancel }: ProfileEditProps) => {
  const [formData, setFormData] = useState<Partial<ProfileData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Initialize form with profile data
    setFormData({
      fullName: profile.fullName || '',
      phone: profile.phone || '',
      iban: profile.iban || '',
      ...(role === 'client' 
        ? {
            nationalId: profile.nationalId || '',
            jobTitle: profile.jobTitle || '',
            monthlyIncome: profile.monthlyIncome || 0,
            monthlyCommitments: profile.monthlyCommitments || 0,
          }
        : {
            licenseNumber: profile.licenseNumber || '',
            commercialRegistryNumber: profile.commercialRegistryNumber || '',
            officeLocation: profile.officeLocation || '',
          }),
    });
  }, [profile, role]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await onSave(formData);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('فشل تحديث الملف الشخصي. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (label: string, id: string, name: string, type = 'text', required = false) => (
    <div key={id} className="px-4 py-3 bg-white border-b border-gray-50">
      <label htmlFor={id} className="block text-sm text-gray-500 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        value={formData[name as keyof typeof formData] || ''}
        onChange={handleChange}
        className="w-full text-right text-gray-900 text-base focus:outline-none"
        required={required}
      />
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen font-sans text-right dir-rtl">
      {/* Header */}
      <div className="bg-white p-4 border-b border-gray-50 flex items-center justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="p-1 rounded-full hover:bg-gray-50"
          disabled={isSubmitting}
        >
          <XMarkIcon className="h-6 w-6 text-gray-700" />
        </button>
        <h2 className="text-lg font-bold text-gray-900">تعديل الملف الشخصي</h2>
        <button
          type="submit"
          form="profile-form"
          className="text-blue-600 font-medium text-sm"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'جاري الحفظ...' : 'حفظ'}
        </button>
      </div>

      <form id="profile-form" onSubmit={handleSubmit} className="pb-20">
        {/* Common Fields */}
        <div className="bg-white rounded-lg shadow-sm mt-4 overflow-hidden">
          {renderField('الاسم الكامل', 'fullName', 'fullName', 'text', true)}
          {renderField('رقم الجوال', 'phone', 'phone', 'tel', true)}
          {renderField('رقم الحساب البنكي (IBAN)', 'iban', 'iban', 'text')}
        </div>

        {/* Role-specific Fields */}
        <div className="bg-white rounded-lg shadow-sm mt-4 overflow-hidden">
          <h3 className="px-4 py-3 text-gray-500 text-sm font-medium border-b border-gray-50">
            {role === 'client' ? 'المعلومات الشخصية' : 'المعلومات المهنية'}
          </h3>
          
          {role === 'client' ? (
            <>
              {renderField('رقم الهوية', 'nationalId', 'nationalId', 'text')}
              {renderField('الوظيفة', 'jobTitle', 'jobTitle', 'text')}
              {renderField('الراتب الشهري', 'monthlyIncome', 'monthlyIncome', 'number')}
              {renderField('الالتزامات الشهرية', 'monthlyCommitments', 'monthlyCommitments', 'number')}
            </>
          ) : (
            <>
              {renderField('رقم الرخصة', 'licenseNumber', 'licenseNumber', 'text')}
              {renderField('رقم السجل التجاري', 'commercialRegistryNumber', 'commercialRegistryNumber', 'text')}
              {renderField('موقع المكتب', 'officeLocation', 'officeLocation', 'text')}
            </>
          )}
        </div>
      </form>

      {/* Error Message */}
      {error && (
        <div className="fixed bottom-4 right-4 left-4 bg-red-50 p-4 rounded-lg shadow-lg flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="mr-3">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
          <button 
            type="button" 
            onClick={() => setError('')}
            className="mr-auto text-red-500 hover:text-red-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileEdit;
